/**
 * Admin Camera Assignments Management
 * Shows camera-to-court assignments and allows editing
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, ChevronLeft, RefreshCw, AlertTriangle,
  CheckCircle2, XCircle, Building2, Link2, Link2Off,
  Search, ChevronDown, ChevronUp, MapPin, History, Clock, User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

// Club card component showing courts and their cameras
const ClubCard = ({ club, availableCameras, onAssignCamera, onUnassignCamera }) => {
  const [expanded, setExpanded] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [selectedCamera, setSelectedCamera] = useState('');

  const totalCourts = club.courts?.length || 0;
  const courtsWithCameras = club.courts?.filter(c => c.has_camera).length || 0;
  const allCourtsHaveCameras = totalCourts > 0 && courtsWithCameras === totalCourts;

  const handleOpenAssignModal = (court) => {
    setSelectedCourt(court);
    setSelectedCamera('');
    setAssignModalOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedCourt || !selectedCamera) return;
    
    await onAssignCamera(club.club_id, selectedCourt.court_id, selectedCamera);
    setAssignModalOpen(false);
    setSelectedCourt(null);
    setSelectedCamera('');
  };

  const handleUnassign = async (courtId, cameraId) => {
    await onUnassignCamera(club.club_id, courtId, cameraId);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`bg-card rounded-xl p-4 border ${allCourtsHaveCameras ? 'border-green-500/30' : 'border-orange-500/30'}`}
    >
      {/* Club Header */}
      <div 
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${allCourtsHaveCameras ? 'bg-green-500/20' : 'bg-orange-500/20'}`}>
            <Building2 className={`w-5 h-5 ${allCourtsHaveCameras ? 'text-green-400' : 'text-orange-400'}`} />
          </div>
          <div>
            <h3 className="font-semibold">{club.club_name}</h3>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {club.city}, {club.country}
            </p>
            {club.club_short_id && (
              <p className="text-xs text-muted-foreground font-mono">ID: {club.club_short_id}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className={`text-sm font-medium ${allCourtsHaveCameras ? 'text-green-400' : 'text-orange-400'}`}>
              {courtsWithCameras}/{totalCourts} canchas
            </p>
            <p className="text-xs text-muted-foreground">con cámara</p>
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Courts List - Expanded */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mt-4 pt-4 border-t border-border"
          >
            <div className="space-y-3">
              {club.courts?.map((court) => (
                <div 
                  key={court.court_id}
                  className={`p-3 rounded-lg ${court.has_camera ? 'bg-green-500/10' : 'bg-orange-500/10'}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${court.has_camera ? 'bg-green-400' : 'bg-orange-400'}`} />
                      <span className="font-medium">Cancha {court.court_number}</span>
                    </div>
                    
                    {!court.has_camera ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenAssignModal(court);
                        }}
                        className="gap-1"
                      >
                        <Link2 className="w-3 h-3" />
                        Asignar cámara
                      </Button>
                    ) : null}
                  </div>
                  
                  {/* Assigned cameras */}
                  {court.cameras?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {court.cameras.map((cam) => (
                        <div 
                          key={cam.camera_id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 bg-card rounded"
                        >
                          <div className="flex items-center gap-2">
                            <Camera className={`w-4 h-4 ${cam.online ? 'text-green-400' : 'text-red-400'}`} />
                            <div>
                              <p className="text-sm font-medium">{cam.name}</p>
                              <p className="text-xs text-muted-foreground">{cam.model || 'Modelo desconocido'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {cam.online ? (
                              <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded">En línea</span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">Offline</span>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnassign(court.court_id, cam.camera_id);
                              }}
                            >
                              <Link2Off className="w-3 h-3 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assign Camera Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary" />
              Asignar Cámara
            </DialogTitle>
            <DialogDescription>
              Asigna una cámara de Hik-Connect a la cancha seleccionada.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-secondary/50 rounded-lg p-3">
              <p className="text-sm"><strong>Club:</strong> {club.club_name}</p>
              <p className="text-sm"><strong>Cancha:</strong> {selectedCourt?.court_number}</p>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Cámara disponible</label>
              <Select value={selectedCamera} onValueChange={setSelectedCamera}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una cámara" />
                </SelectTrigger>
                <SelectContent>
                  {availableCameras.filter(c => !c.is_assigned).map((cam) => (
                    <SelectItem key={cam.id} value={cam.id}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${cam.online ? 'bg-green-400' : 'bg-red-400'}`} />
                        {cam.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableCameras.filter(c => !c.is_assigned).length === 0 && (
                <p className="text-xs text-orange-400 mt-1">
                  No hay cámaras disponibles. Todas están asignadas.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAssign} disabled={!selectedCamera}>
              <Link2 className="w-4 h-4 mr-2" />
              Asignar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

// Main component
export default function AdminCameraAssignments() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState([]);
  const [unassignedCameras, setUnassignedCameras] = useState([]);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [assignmentsRes, availableRes] = await Promise.all([
        adminAPI.getCameraAssignments(),
        adminAPI.getAvailableCameras()
      ]);

      setOverview(assignmentsRes.data.overview || []);
      setUnassignedCameras(assignmentsRes.data.unassigned_cameras || []);
      setStats(assignmentsRes.data.stats || {});
      setAvailableCameras(availableRes.data.cameras || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Error cargando datos de cámaras');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    try {
      setAuditLoading(true);
      const res = await adminAPI.getCameraAuditLog({ limit: 50 });
      setAuditLogs(res.data.logs || []);
    } catch (err) {
      console.error('Error fetching audit log:', err);
      toast.error('Error cargando historial');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (showAuditLog) {
      fetchAuditLog();
    }
  }, [showAuditLog, fetchAuditLog]);

  const handleAssignCamera = async (clubId, courtId, cameraId) => {
    try {
      await adminAPI.assignCameraToCourtAdmin(clubId, courtId, cameraId);
      toast.success('Cámara asignada correctamente');
      fetchData();
    } catch (err) {
      console.error('Assign error:', err);
      toast.error(err.response?.data?.detail || 'Error asignando cámara');
    }
  };

  const handleUnassignCamera = async (clubId, courtId, cameraId) => {
    try {
      await adminAPI.unassignCameraFromCourt(clubId, courtId, cameraId);
      toast.success('Cámara desasignada correctamente');
      fetchData();
    } catch (err) {
      console.error('Unassign error:', err);
      toast.error(err.response?.data?.detail || 'Error desasignando cámara');
    }
  };

  // Filter clubs by search
  const filteredClubs = overview.filter(club =>
    club.club_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    club.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background overflow-x-hidden admin-mobile">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/admin')}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-primary" />
                  Asignación de Cámaras
                </h1>
                <p className="text-sm text-muted-foreground">
                  Gestiona qué cámara graba cada cancha
                </p>
              </div>
            </div>
            <div className="w-full sm:w-auto flex flex-wrap items-center justify-end gap-2">
              <Button 
                variant={showAuditLog ? "default" : "outline"} 
                onClick={() => setShowAuditLog(!showAuditLog)}
                className="gap-2"
              >
                <History className="w-4 h-4" />
                Historial
              </Button>
              <Button onClick={fetchData} disabled={loading} className="gap-2">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6 overflow-x-hidden">
        {/* Audit Log Section */}
        <AnimatePresence>
          {showAuditLog && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-card border border-border rounded-xl overflow-hidden"
            >
              <div className="p-4 border-b border-border bg-muted/30">
                <h2 className="font-semibold flex items-center gap-2">
                  <History className="w-4 h-4 text-primary" />
                  Historial de Cambios
                </h2>
                <p className="text-sm text-muted-foreground">
                  Registro de asignaciones y desasignaciones de cámaras
                </p>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {auditLoading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No hay cambios registrados
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {auditLogs.map((log) => (
                      <div key={log.id} className="p-3 hover:bg-muted/30 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className={`p-1.5 rounded-full ${
                            log.action === 'assign' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {log.action === 'assign' ? (
                              <Link2 className="w-3.5 h-3.5" />
                            ) : (
                              <Link2Off className="w-3.5 h-3.5" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">
                              {log.action === 'assign' ? 'Cámara asignada' : 'Cámara desasignada'}
                              <span className="text-muted-foreground font-normal">
                                {' '}en {log.club_name} - Cancha {log.court_number}
                              </span>
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              ID: {log.camera_id}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {log.admin_email}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(log.timestamp).toLocaleString('es-MX', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-xl p-4 border border-border">
            <p className="text-sm text-muted-foreground">Total Clubs</p>
            <p className="text-2xl font-bold">{stats.total_clubs || 0}</p>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <p className="text-sm text-muted-foreground">Canchas con cámara</p>
            <p className="text-2xl font-bold text-green-400">{stats.courts_with_cameras || 0}</p>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <p className="text-sm text-muted-foreground">Canchas sin cámara</p>
            <p className="text-2xl font-bold text-orange-400">{stats.courts_without_cameras || 0}</p>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <p className="text-sm text-muted-foreground">Cámaras sin asignar</p>
            <p className="text-2xl font-bold text-blue-400">{stats.unassigned_cameras || 0}</p>
          </div>
        </div>

        {/* Unassigned Cameras Warning */}
        {unassignedCameras.length > 0 && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Camera className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-400">Cámaras sin asignar</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Las siguientes cámaras están disponibles para asignar a canchas:
                </p>
                <div className="flex flex-wrap gap-2">
                  {unassignedCameras.map((cam) => (
                    <span 
                      key={cam.camera_id}
                      className="px-2 py-1 bg-card rounded text-xs flex items-center gap-1"
                    >
                      <span className={`w-2 h-2 rounded-full ${cam.online ? 'bg-green-400' : 'bg-red-400'}`} />
                      {cam.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Courts without cameras warning */}
        {stats.courts_without_cameras > 0 && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5" />
              <div>
                <h3 className="font-medium text-orange-400">Canchas sin cámara asignada</h3>
                <p className="text-sm text-muted-foreground">
                  Hay {stats.courts_without_cameras} canchas que no tienen cámara asignada. 
                  Sin cámara, los usuarios no podrán grabar clips en estas canchas.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar club o ciudad..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Clubs List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-4 text-muted-foreground">Cargando asignaciones...</p>
            </div>
          ) : filteredClubs.length === 0 ? (
            <div className="text-center py-12">
              <XCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No se encontraron clubs</h3>
              <p className="text-muted-foreground">Intenta con otro término de búsqueda</p>
            </div>
          ) : (
            <AnimatePresence>
              {filteredClubs.map((club) => (
                <ClubCard
                  key={club.club_id}
                  club={club}
                  availableCameras={availableCameras}
                  onAssignCamera={handleAssignCamera}
                  onUnassignCamera={handleUnassignCamera}
                />
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* All cameras assigned message */}
        {stats.courts_without_cameras === 0 && stats.total_courts > 0 && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
            <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <h3 className="font-medium text-green-400">¡Todas las canchas tienen cámara!</h3>
            <p className="text-sm text-muted-foreground">
              Todas las canchas están listas para grabar clips.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
